import { useEffect, useMemo, useState } from 'react';
import { Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { PageHeader, EmptyState, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Checkbox, Select, Separator } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import { cn, humanise, titleCase } from '@/lib/utils';
import type { PermissionScope, Role } from '@/lib/types';
import { useDeleteRole, usePermissionCatalogue, useRoles, useSaveRole } from './api';

type Draft = {
  id?: string;
  name: string;
  description: string;
  isSystem: boolean;
  grants: Map<string, PermissionScope>;
};

const SCOPE_HINT: Record<PermissionScope, string> = {
  OWN: 'Only records they own',
  TEAM: 'Records owned by their team, or people reporting to them',
  ALL: 'Every record in the organisation',
};

function toDraft(role: Role): Draft {
  return {
    id: role.id,
    name: role.name,
    description: role.description ?? '',
    isSystem: role.isSystem,
    grants: new Map(role.permissions.map((p) => [p.key, p.scope])),
  };
}

const EMPTY_DRAFT: Draft = {
  name: '',
  description: '',
  isSystem: false,
  grants: new Map(),
};

export function RolesPage() {
  const { data: roles = [], isLoading } = useRoles();
  const { data: catalogue = [] } = usePermissionCatalogue();
  const save = useSaveRole();
  const remove = useDeleteRole();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // "New role" clears the selection on purpose. Without this flag the
  // first-role auto-select below would immediately fill the blank form back
  // in with Accountant, and the button would look as if it did nothing.
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);

  // Select the first role once the list arrives.
  useEffect(() => {
    if (!selectedId && !creating && roles.length > 0) {
      setSelectedId(roles[0].id);
      setDraft(toDraft(roles[0]));
    }
  }, [roles, selectedId, creating]);

  const selected = useMemo(
    () => roles.find((role) => role.id === selectedId) ?? null,
    [roles, selectedId],
  );

  const pick = (role: Role) => {
    setCreating(false);
    setSelectedId(role.id);
    setDraft(toDraft(role));
    setError(null);
  };

  const startNew = () => {
    setCreating(true);
    setSelectedId(null);
    setDraft({ ...EMPTY_DRAFT, grants: new Map() });
    setError(null);
  };

  const toggleGrant = (key: string) => {
    const grants = new Map(draft.grants);
    if (grants.has(key)) grants.delete(key);
    else grants.set(key, 'ALL');
    setDraft({ ...draft, grants });
  };

  const setScope = (key: string, scope: PermissionScope) => {
    const grants = new Map(draft.grants);
    grants.set(key, scope);
    setDraft({ ...draft, grants });
  };

  const toggleModule = (moduleKeys: string[], on: boolean) => {
    const grants = new Map(draft.grants);
    for (const key of moduleKeys) {
      if (on) grants.set(key, grants.get(key) ?? 'ALL');
      else grants.delete(key);
    }
    setDraft({ ...draft, grants });
  };

  const onSave = async () => {
    setError(null);
    try {
      const saved = await save.mutateAsync({
        ...(draft.id ? { id: draft.id } : {}),
        name: draft.name,
        description: draft.description,
        permissions: [...draft.grants.entries()].map(([key, scope]) => ({ key, scope })),
      });
      setCreating(false);
      setSelectedId(saved.id);
      setDraft(toDraft(saved));
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save this role.');
    }
  };

  const onDelete = async () => {
    if (!draft.id) return;
    setError(null);
    try {
      await remove.mutateAsync(draft.id);
      setCreating(false);
      setSelectedId(null);
      setDraft(EMPTY_DRAFT);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not delete this role.');
    }
  };

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Roles" description="What each kind of person can see and do." />
        <TableSkeleton />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Roles"
        description="Roles are data, not code. Adjust a system role or build your own."
        actions={
          <Can permission="admin.role.create">
            <Button onClick={startNew}>
              <Plus className="h-4 w-4" />
              New role
            </Button>
          </Can>
        }
      />

      <div className="grid gap-5 lg:grid-cols-[16rem_1fr]">
        <nav className="space-y-1">
          {creating && (
            <div className="rounded-md border border-primary bg-primary/5 px-3 py-2.5">
              <span className="text-sm font-medium">{draft.name.trim() || 'New role'}</span>
              <div className="mt-0.5 text-xs text-muted-foreground">Not saved yet</div>
            </div>
          )}
          {roles.map((role) => (
            <button
              key={role.id}
              onClick={() => pick(role)}
              className={cn(
                'w-full rounded-md border px-3 py-2.5 text-start transition-colors',
                selectedId === role.id ? 'border-primary bg-primary/5' : 'hover:bg-accent',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium">{role.name}</span>
                {role.isSystem && (
                  <Badge variant="secondary" className="shrink-0 text-[10px]">
                    System
                  </Badge>
                )}
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {role.userCount} {role.userCount === 1 ? 'person' : 'people'} ·{' '}
                {role.permissions.length} permissions
              </div>
            </button>
          ))}
          {selectedId === null && draft.name === '' && roles.length === 0 && (
            <EmptyState
              icon={<ShieldCheck className="h-8 w-8" />}
              title="No roles yet"
              description="Seed the system roles or create your first one."
            />
          )}
        </nav>

        <Card className="p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="role-name">Role name</Label>
              <Input
                id="role-name"
                value={draft.name}
                disabled={draft.isSystem}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
              {draft.isSystem && (
                <p className="text-xs text-muted-foreground">
                  System roles keep their name so automation that depends on it keeps working. Their
                  permissions can still be tuned.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="role-description">Description</Label>
              <Input
                id="role-description"
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              />
            </div>
          </div>

          <Separator className="my-5" />

          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium">Permissions</h3>
              <p className="text-xs text-muted-foreground">
                {draft.grants.size} of{' '}
                {catalogue.reduce((sum, module) => sum + module.permissions.length, 0)} granted
              </p>
            </div>
          </div>

          <div className="scrollbar-thin max-h-[28rem] space-y-4 overflow-y-auto pe-1">
            {catalogue.map((module) => {
              const keys = module.permissions.map((p) => p.key);
              const grantedCount = keys.filter((key) => draft.grants.has(key)).length;
              const allOn = grantedCount === keys.length;

              return (
                <div key={module.module} className="rounded-md border">
                  <div className="flex items-center justify-between border-b bg-muted/40 px-3 py-2">
                    <span className="text-sm font-medium">{titleCase(module.module)}</span>
                    <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                      {grantedCount}/{keys.length}
                      <Checkbox
                        checked={allOn}
                        onCheckedChange={() => toggleModule(keys, !allOn)}
                      />
                    </label>
                  </div>

                  <div className="divide-y">
                    {groupByResource(module.permissions).map(([resource, permissions]) => (
                      <div key={resource} className="px-3 py-2">
                        <div className="mb-1.5 text-xs font-medium text-muted-foreground">
                          {titleCase(resource)}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-2">
                          {permissions.map((permission) => {
                            const granted = draft.grants.has(permission.key);
                            return (
                              <div key={permission.key} className="flex items-center gap-1.5">
                                <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                                  <Checkbox
                                    checked={granted}
                                    onCheckedChange={() => toggleGrant(permission.key)}
                                  />
                                  {humanise(permission.key)}
                                </label>
                                {granted && (
                                  <Select
                                    value={draft.grants.get(permission.key)}
                                    onChange={(event) =>
                                      setScope(permission.key, event.target.value as PermissionScope)
                                    }
                                    title={SCOPE_HINT[draft.grants.get(permission.key) ?? 'ALL']}
                                    className="h-6 w-auto px-1 text-xs"
                                  >
                                    <option value="OWN">Own</option>
                                    <option value="TEAM">Team</option>
                                    <option value="ALL">All</option>
                                  </Select>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <FormError message={error} />

          <div className="mt-5 flex items-center justify-between">
            <Can permission="admin.role.delete">
              {draft.id && !draft.isSystem ? (
                <Button variant="ghost" size="sm" onClick={onDelete} disabled={remove.isPending}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                  Delete role
                </Button>
              ) : (
                <span />
              )}
            </Can>

            <Can anyOf={['admin.role.create', 'admin.role.update']}>
              <Button onClick={onSave} disabled={save.isPending || draft.name.trim().length < 2}>
                {save.isPending ? 'Saving…' : draft.id ? 'Save changes' : 'Create role'}
              </Button>
            </Can>
          </div>

          {selected && (
            <p className="mt-3 text-xs text-muted-foreground">
              {selected.userCount} {selected.userCount === 1 ? 'person holds' : 'people hold'} this
              role. Changes take effect on their next request.
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}

function groupByResource(permissions: { key: string; resource: string }[]) {
  const groups = new Map<string, { key: string; resource: string }[]>();
  for (const permission of permissions) {
    const list = groups.get(permission.resource) ?? [];
    list.push(permission);
    groups.set(permission.resource, list);
  }
  return [...groups.entries()];
}
